import { CaretDownOutlined, InfoCircleFilled, KeyOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Avatar, Col, Dropdown, Flex, Row, Space, Typography } from 'antd';
import { t } from 'i18next';
import type { MouseEvent } from 'react';
import { Fragment, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DepartmentEn } from 'src/constants/department.enum';
import urlPath from 'src/constants/urlPath';
import { AppContext } from 'src/contexts/app.context';
import usePermission from 'src/hooks/usePermission';
import Config from 'src/pages/v2/Config/Setting';
import ModalConfirm from 'src/pages/v2/HrTicket/SalaryTicket/ModalConfirm';
import ModalSalary from 'src/pages/v2/HrTicket/SalaryTicket/ModalSalary';
import type { UserInfo } from 'src/types/master_data/employee.type';
import { v4 as uuid } from 'uuid';
import './header.scss';
import ModulesMenuButton from './ModulesMenu/ModulesMenuButton';
import NotificationComponent from './Notification';
import { clearLS, getAccessTokenFromLS } from '@martech-solution/utils/utils';
import ChangePassword from './ChangePassword';

const { Text } = Typography;

interface MenuType {
  id: string;
  label: string;
  location: string;
  submenus?: Submenu[];
  mainLocation?: string;
}

interface Submenu {
  id: string;
  label: string;
  location: string;
  onClick?: () => void;
  isHidden?: boolean;
}

// const menuCorporateOperationList = (paths: string[]) => {
//   const listCorporateOperationMenu = [
//     {
//       id: uuid(),
//       label: 'Asset Management',
//       location: urlPath.corporateOperation.location,
//       mainLocation: urlPath.corporateOperation.location,
//       submenus: [
//         {
//           id: uuid(),
//           label: t('v2.officeTicket.handoverTicket'),
//           location: urlPath.myHandoverTicket.location
//         }
//       ]
//     }
//   ];

//   !paths?.includes(urlPath.handoverTicket.location) && listCorporateOperationMenu.splice(0, 1);

//   return listCorporateOperationMenu;
// };

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = getAccessTokenFromLS();

  const [openChangePassword, setOpenChangePassword] = useState<boolean>(false);
  const [openConfig, setOpenConfig] = useState<boolean>(false);

  const isTicketPage = location.pathname.includes(urlPath.ticket.location);
  const isHrTicketPage = location.pathname.includes(urlPath.hrTicket.location);
  const isHrConfirmTicketPage = location.pathname.includes(urlPath.hrConfirm.location);
  const isOfficeTicketPage = location.pathname.includes(urlPath.corporateOperation.location);
  const isExternalPage = location.pathname === urlPath.homeSub.location || location.pathname.includes('/s/');
  const isHelpDeskApp = location.pathname.includes(urlPath.ticketRaising.location);
  const isShowHeaderMenu = isTicketPage || isHrTicketPage || isOfficeTicketPage || isHrConfirmTicketPage;

  const { userInfo, setUserInfo, setUserInfoQr, isUserAdmin, isSupportUser, setTicketSelected } =
    useContext(AppContext);

  // modal salary
  const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
  const [openModalSalary, setOpenModalSalary] = useState(false);
  const [salaryTicketData, setSalaryTicketData] = useState<any>(null);

  const profile = JSON.parse(localStorage.getItem('profile') || '{}');

  const overtimePermission = usePermission({ path: urlPath.overtimeTicket.location });
  const businessTripPermission = usePermission({ path: urlPath.businessTripTicket.location });
  const additionalWorkingTimePermission = usePermission({ path: urlPath.additionalWorkingTimeTicket.location });
  const hrConfirmLeavePermission = usePermission({ path: urlPath.hrConfirmLeaveTicket.location });
  const hrConfirmResignationPermission = usePermission({ path: urlPath.hrConfirmResignationTicket.location });
  const hrConfirmBusinessTripPermission = usePermission({ path: urlPath.hrConfirmBusinessTripTicket.location });
  const hrConfirmOvertimePermission = usePermission({ path: urlPath.hrConfirmOvertimeTicket.location });
  const hrConfirmTransportationPermission = usePermission({ path: urlPath.hrConfirmTransportationTicket.location });
  const hrConfirmAdditionalWorkingTimePermission = usePermission({
    path: urlPath.hrConfirmAdditionalWorkingTimeTicket.location
  });

  const menuHelpDeskList = (userInfo: UserInfo | null, isUserAdmin: boolean | undefined): MenuType[] => {
    let listHelpDeskMenu = [
      {
        id: '1',
        label: 'Ticket Raising',
        location: urlPath.myTicket.location,
        mainLocation: urlPath.ticket.location
      },
      {
        id: '2',
        label: 'HR Ticket',
        location: urlPath.hrTicket.location,
        submenus: [
          {
            id: uuid(),
            label: t('v2.sidebar.hrTicket.leaveTicket'),
            location: urlPath.myLeaveTicket.location
          },
          {
            id: uuid(),
            label: t('v2.sidebar.hrTicket.overtimeTicket'),
            location: urlPath.myOvertimeTicket.location,
            isHidden: !overtimePermission.isRead
          },
          {
            id: uuid(),
            label: t('v2.sidebar.hrTicket.businessTripTicket'),
            location: urlPath.myBusinessTripTicket.location,
            isHidden: !businessTripPermission.isRead
          },
          {
            id: uuid(),
            label: t('v2.sidebar.hrTicket.transportationTicket'),
            location: urlPath.myTransportationTicket.location
          },
          // {
          //   id: uuid(),
          //   label: t('v2.sidebar.hrTicket.purchaseRequestTicket'),
          //   location: urlPath.myPurchaseRequestTicket.location
          // },
          // {
          //   id: uuid(),
          //   label: t('v2.sidebar.hrTicket.equipmentSupplyRequestTicket'),
          //   location: urlPath.myEquipmentSupplyRequestTicket.location
          // },
          {
            id: uuid(),
            label: t('v2.sidebar.hrTicket.additionalWorkingTimeTicket'),
            location: urlPath.myAdditionalWorkingTimeTicket.location,
            isHidden: !additionalWorkingTimePermission.isRead
          },
          {
            id: uuid(),
            label: t('v2.sidebar.hrTicket.resignationTicket'),
            location: urlPath.myResignationTicket.location
          }
          // {
          //   id: uuid(),
          //   label: t('Phiếu lương'),
          //   location: '',
          //   onClick: handleConfirmModal
          // }
        ]
      },
      {
        id: '3',
        label: 'HR Chốt phiếu',
        location: urlPath.hrConfirm.location,
        submenus: [
          {
            id: uuid(),
            label: t('v2.sidebar.hrTicket.leaveTicket'),
            location: urlPath.hrConfirmLeaveTicket.location,
            isHidden: !hrConfirmLeavePermission.isRead
          },
          {
            id: uuid(),
            label: t('v2.sidebar.hrTicket.overtimeTicket'),
            location: urlPath.hrConfirmOvertimeTicket.location,
            isHidden: !hrConfirmOvertimePermission.isRead
          },
          {
            id: uuid(),
            label: t('v2.sidebar.hrTicket.businessTripTicket'),
            location: urlPath.hrConfirmBusinessTripTicket.location,
            isHidden: !hrConfirmBusinessTripPermission.isRead
          },
          {
            id: uuid(),
            label: t('v2.sidebar.hrTicket.transportationTicket'),
            location: urlPath.hrConfirmTransportationTicket.location,
            isHidden: !hrConfirmTransportationPermission.isRead || userInfo?.departmentId !== DepartmentEn.hr
          },
          {
            id: uuid(),
            label: t('v2.sidebar.hrTicket.resignationTicket'),
            location: urlPath.hrConfirmResignationTicket.location,
            isHidden: !hrConfirmResignationPermission.isRead
          },
          {
            id: uuid(),
            label: t('v2.sidebar.hrConfirm.additionalWorkingTimeTicket'),
            location: urlPath.hrConfirmAdditionalWorkingTimeTicket.location,
            isHidden: !hrConfirmAdditionalWorkingTimePermission.isRead
          }
        ]
      }
    ];

    if (isUserAdmin) return listHelpDeskMenu;

    // Filter submenus: chỉ giữ lại những item có isHidden = false hoặc undefined
    const filteredMenus = listHelpDeskMenu.map((menu) => ({
      ...menu,
      submenus: menu.submenus?.filter((submenu) => !submenu.isHidden)
    }));

    // Filter main menu items: loại bỏ những menu có submenus rỗng hoặc không có visible submenus
    return filteredMenus.filter((menu) => {
      // Nếu menu không có submenus, giữ lại
      if (!menu.submenus) {
        return true;
      }
      // Nếu menu có submenus, chỉ giữ lại khi có ít nhất 1 submenu visible
      return menu.submenus.length > 0;
    });

    // Permission checks for HR tickets
    // !paths?.includes(urlPath.overtimeTicket.location) &&
    //   (listHelpDeskMenu[1] = {
    //     ...listHelpDeskMenu[1],
    //     submenus: listHelpDeskMenu[1]?.submenus?.filter(
    //       (item) => item.location !== urlPath.myOvertimeTicket.location
    //     ) as Submenu[],
    //     mainLocation: undefined
    //   });

    // !paths?.includes(urlPath.transportationTicket.location) &&
    //   (listHelpDeskMenu[1] = {
    //     ...listHelpDeskMenu[1],
    //     submenus: listHelpDeskMenu[1]?.submenus?.filter(
    //       (item) => item.location !== urlPath.myTransportationTicket.location
    //     ) as Submenu[],
    //     mainLocation: undefined
    //   });

    // !paths?.includes(urlPath.businessTripTicket.location) &&
    //   (listHelpDeskMenu[1] = {
    //     ...listHelpDeskMenu[1],
    //     submenus: listHelpDeskMenu[1]?.submenus?.filter(
    //       (item) => item.location !== urlPath.myBusinessTripTicket.location
    //     ) as Submenu[],
    //     mainLocation: undefined
    //   });

    // !paths?.includes(urlPath.additionalWorkingTimeTicket.location) &&
    //   (listHelpDeskMenu[1] = {
    //     ...listHelpDeskMenu[1],
    //     submenus: listHelpDeskMenu[1]?.submenus?.filter(
    //       (item) => item.location !== urlPath.myAdditionalWorkingTimeTicket.location
    //     ) as Submenu[],
    //     mainLocation: undefined
    //   });

    // // HR Confirm permission checks
    // !paths?.includes(urlPath.hrConfirm.location) &&
    //   !paths?.includes(urlPath.hrConfirmLeaveTicket.location) &&
    //   (listHelpDeskMenu[2] = {
    //     ...listHelpDeskMenu[2],
    //     submenus: listHelpDeskMenu[2]?.submenus?.filter((item) => {
    //       return item.location !== urlPath.hrConfirmLeaveTicket.location;
    //     }) as Submenu[],
    //     mainLocation: undefined
    //   });

    // !paths?.includes(urlPath.hrConfirm.location) &&
    //   !paths?.includes(urlPath.hrConfirmResignationTicket.location) &&
    //   (listHelpDeskMenu[2] = {
    //     ...listHelpDeskMenu[2],
    //     submenus: listHelpDeskMenu[2]?.submenus?.filter((item) => {
    //       return item.location !== urlPath.hrConfirmResignationTicket.location;
    //     }) as Submenu[],
    //     mainLocation: undefined
    //   });

    // !paths?.includes(urlPath.hrConfirm.location) &&
    //   !paths?.includes(urlPath.hrConfirmBusinessTripTicket.location) &&
    //   (listHelpDeskMenu[2] = {
    //     ...listHelpDeskMenu[2],
    //     submenus: listHelpDeskMenu[2]?.submenus?.filter((item) => {
    //       return item.location !== urlPath.hrConfirmBusinessTripTicket.location;
    //     }) as Submenu[],
    //     mainLocation: undefined
    //   });

    // !paths?.includes(urlPath.hrConfirm.location) &&
    //   !paths?.includes(urlPath.hrConfirmOvertimeTicket.location) &&
    //   (listHelpDeskMenu[2] = {
    //     ...listHelpDeskMenu[2],
    //     submenus: listHelpDeskMenu[2]?.submenus?.filter((item) => {
    //       return item.location !== urlPath.hrConfirmOvertimeTicket.location;
    //     }) as Submenu[],
    //     mainLocation: undefined
    //   });

    // (!paths?.includes(urlPath.hrConfirmTransportationTicket.location) || userInfo?.departmentId !== DepartmentEn.hr) &&
    //   (listHelpDeskMenu[2] = {
    //     ...listHelpDeskMenu[2],
    //     submenus: listHelpDeskMenu[2]?.submenus?.filter((item) => {
    //       return item.location !== urlPath.hrConfirmTransportationTicket.location;
    //     }) as Submenu[],
    //     mainLocation: undefined
    //   });

    // !paths?.includes(urlPath.hrConfirmLeaveTicket.location) &&
    //   !paths?.includes(urlPath.hrConfirmResignationTicket.location) &&
    //   !paths?.includes(urlPath.hrConfirmBusinessTripTicket.location) &&
    //   !paths?.includes(urlPath.hrConfirmOvertimeTicket.location) &&
    //   (!paths?.includes(urlPath.hrConfirmTransportationTicket.location) ||
    //     userInfo?.departmentId !== DepartmentEn.hr) &&
    //   (listHelpDeskMenu = listHelpDeskMenu.filter((item) => item.location !== urlPath.hrConfirm.location));

    // return listHelpDeskMenu;
  };

  const handleNavigateToEmployeeInfo = () => {
    navigate(urlPath.employeeInfo.location);
  };

  const handleChangePassword = () => {
    setOpenChangePassword(true);
  };

  const handleLogout = async () => {
    clearLS();
    const returnUrl = window.location.pathname;
    localStorage.setItem('returnUrl', returnUrl);
    setUserInfo(null);
    setUserInfoQr(undefined);
    navigate(urlPath.signoutOidc.location);
  };

  const items: MenuProps['items'] = [
    {
      key: '1',
      onClick: () => {
        handleNavigateToEmployeeInfo();
      },
      label: (
        <Flex className='profile-menu-action'>
          <InfoCircleFilled />
          <span>Thông tin cá nhân</span>
        </Flex>
      )
    },
    {
      key: '2',
      onClick: () => {
        handleChangePassword();
      },
      label: (
        <Flex className='profile-menu-action'>
          <KeyOutlined />
          <span>Thay đổi mật khẩu</span>
        </Flex>
      )
    },
    {
      key: '3',
      onClick: () => {
        handleLogout();
      },
      label: (
        <Flex className='profile-menu-action'>
          <LogoutOutlined />
          <span>Đăng xuất</span>
        </Flex>
      )
    }
  ];

  const handleClickMenuItem = (location: string, e: MouseEvent<HTMLLIElement, globalThis.MouseEvent>) => {
    if (
      location === urlPath.myTicket.location ||
      location === urlPath.biometricLog.location ||
      location === urlPath.hrConfirmLeaveTicket.location ||
      location === urlPath.hrConfirmResignationTicket.location ||
      location === urlPath.hrConfirmAdditionalWorkingTimeTicket.location
    ) {
      navigate(location);
    } else e.preventDefault();
  };

  const getMenuList = () => {
    if (isHelpDeskApp) {
      return menuHelpDeskList(userInfo, isUserAdmin);
    } else return [];
    // if (isCorporateOperationApp) {
    //   return menuCorporateOperationList(paths);
    // }
  };

  // Check if we're on the Home page
  // const isHomePage = location.pathname === urlPath.home.location;

  return (
    <Fragment>
      {/* Full Width Header - Always visible */}
      <div className='header-full-width'>
        <Row align='middle' justify='space-between' className='header-main-content'>
          {/* Left side - App Name & Navigation */}
          <Col flex='auto'>
            <Space size='large' align='center'>
              <div className='app-brand' onClick={() => navigate(urlPath.home.location)}>
                <Typography.Title level={3} className='app-name'>
                  Helpdesk
                </Typography.Title>
                <Text className='app-subtitle'>Support System</Text>
              </div>

              {/* Navigation Menu for ticket pages */}
              {isShowHeaderMenu && (
                <ul className='header-menu-list'>
                  {getMenuList()?.map((x, index, xs) => (
                    <Fragment key={x.id}>
                      <li
                        className={`header-menu-item ${
                          location.pathname.includes(x.mainLocation ? x.mainLocation : x.location) && 'actived'
                        }`}
                        onClick={(e) => {
                          setTicketSelected(undefined);
                          handleClickMenuItem(x.location, e);
                        }}
                      >
                        {x.label}
                        {x.submenus && x.submenus?.length > 0 && (
                          <ul className='menu-dropdown'>
                            {x.submenus?.map((submenu) => (
                              <li
                                className={`header-menu-sub-item ${
                                  submenu.location && location.pathname.includes(submenu.location) && 'actived'
                                }`}
                                key={submenu.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if ('onClick' in submenu && submenu.onClick) {
                                    submenu.onClick();
                                  } else if (submenu.location) {
                                    navigate(submenu.location);
                                  }
                                  // setIsTicketDetailCollapsed(true);
                                  setTicketSelected(undefined);
                                }}
                              >
                                {submenu.label}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                      {/* {index !== xs.length - 1 && <Divider type='vertical' className='menu-divider' />} */}
                    </Fragment>
                  ))}
                </ul>
              )}
            </Space>{' '}
          </Col>

          {/* Right side - Actions & User Profile */}
          <Col>
            <Space size='large' className='header-actions'>
              {/* Module Grid Menu */}
              {!isSupportUser && <ModulesMenuButton />}

              {/* Notification Bell */}
              {token && !isSupportUser && <NotificationComponent />}

              {/* User Profile */}
              {!isExternalPage && (
                <Dropdown
                  menu={{ items }}
                  trigger={['hover']}
                  mouseEnterDelay={0.1}
                  mouseLeaveDelay={0.1}
                  arrow={false}
                  placement='bottomRight'
                >
                  <Space className='user-profile-section' size={[12, 0]}>
                    <div className='user-info'>
                      <Text className='user-name'>{userInfo?.fullName || 'User'}</Text>
                      <Text className='user-role'>{profile?.profile?.name}</Text>
                    </div>
                    <Avatar size={40} icon={<UserOutlined />} className='user-avatar' />
                    <CaretDownOutlined className='dropdown-arrow' />
                  </Space>
                </Dropdown>
              )}
            </Space>
          </Col>
        </Row>
      </div>

      {/* Modals */}
      {openChangePassword && <ChangePassword open={openChangePassword} setOpen={setOpenChangePassword} />}
      {openConfig && <Config open={openConfig} setOpen={setOpenConfig} />}
      {openModalConfirm && (
        <ModalConfirm
          open={openModalConfirm}
          setOpen={setOpenModalConfirm}
          onSuccess={(data) => {
            setSalaryTicketData(data);
            setOpenModalSalary(true);
          }}
        />
      )}
      {openModalSalary && salaryTicketData && (
        <ModalSalary
          open={openModalSalary}
          onClose={() => setOpenModalSalary(false)}
          salaryTicket={salaryTicketData.salaryTicket}
        />
      )}
    </Fragment>
  );
};

export default Header;
